import * as React from 'react'
import { Check, CircleAlert, CircleHelp } from 'lucide-react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import type { RawCareGaps } from '@/features/patients/types'
import type { CareGap, CareGapYear } from '@/features/patients/utils/careGaps'
import {
  groupCareGapsByYear,
  normalizeCareGaps,
} from '@/features/patients/utils/careGaps'
import { cn } from '@/lib/utils'

const UNDATED_YEAR = 'undated'

function yearKey(year: number | null): string {
  return year === null ? UNDATED_YEAR : String(year)
}

function GapRow({
  gap,
  tone,
}: {
  gap: CareGap
  tone: 'open' | 'met' | 'unknown'
}) {
  const Icon =
    tone === 'open' ? CircleAlert : tone === 'met' ? Check : CircleHelp
  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-sm',
        tone === 'open' &&
          'bg-warning-subtle-bg border-warning-subtle-border text-warning-high-contrast',
        tone === 'met' &&
          'bg-success-subtle-bg border-success-subtle-border text-text-body',
        tone === 'unknown' && 'bg-neutral-subtle-bg text-text-body'
      )}>
      <Icon
        className={cn(
          'size-4 shrink-0',
          tone === 'open' && 'text-warning-solid',
          tone === 'met' && 'text-success-solid',
          tone === 'unknown' && 'text-neutral-solid'
        )}
      />
      <span className="min-w-0 flex-1 break-words">{gap.measure}</span>
      <span className="text-muted-foreground shrink-0 text-xs">
        {tone === 'open'
          ? 'eligible · not satisfied'
          : tone === 'met'
            ? 'satisfied'
            : 'status not recognized'}
      </span>
    </li>
  )
}

function GapGroup({
  heading,
  gaps,
  tone,
  badge,
}: {
  heading: string
  gaps: CareGap[]
  tone: 'open' | 'met' | 'unknown'
  badge: React.ComponentProps<typeof Badge>['variant']
}) {
  if (gaps.length === 0) return null
  return (
    <div>
      <p className="text-muted-foreground mb-1.5 flex items-center gap-2 text-xs font-medium">
        {heading}
        <Badge variant={badge}>{gaps.length}</Badge>
      </p>
      <ul className="flex flex-col gap-1">
        {gaps.map((gap) => (
          <GapRow key={gap.raw} gap={gap} tone={tone} />
        ))}
      </ul>
    </div>
  )
}

/**
 * HEDIS measure status for one measurement year.
 *
 * Three states are deliberately distinct, because collapsing them misreports
 * the client's care:
 *   no data     — never evaluated; not in any measure population. NOT "no gaps".
 *   all met     — evaluated, every measure satisfied.
 *   open gaps   — in the denominator and not satisfied; the actionable case.
 *
 * Only about one client in sixteen carries gap data at all, so the first state
 * is the common one and has to say what it means rather than reading as a
 * clean bill of health.
 */
export function ClientCareGapsCard({
  careGaps,
}: {
  careGaps: RawCareGaps | null
}) {
  const groups = React.useMemo(
    () => groupCareGapsByYear(normalizeCareGaps(careGaps)),
    [careGaps]
  )

  // Selection resets to the newest year when the sheet moves to another
  // client: the caller keys this card by patient id, so it remounts.
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null)

  const active: CareGapYear | undefined =
    groups.find((group) => yearKey(group.year) === selectedKey) ?? groups[0]

  const title = active ? `Care gaps · ${active.year ?? 'undated'}` : 'Care gaps'

  return (
    <SheetSectionCard
      title={
        <span className="flex w-full items-center justify-between gap-3">
          <span>{title}</span>
          {groups.length > 1 ? (
            <Select
              value={yearKey(active?.year ?? null)}
              onValueChange={setSelectedKey}>
              {/* A text action, as in the design: the selected year is
                  already in the title, so the trigger only names the action. */}
              <SelectTrigger
                size="sm"
                className="text-primary-low-contrast gap-0 border-0 bg-transparent font-medium tracking-normal shadow-none hover:underline focus-visible:border-0 focus-visible:underline data-[size=sm]:h-auto data-[size=sm]:px-0 [&>svg]:hidden"
                aria-label="Change measurement year">
                Change year
              </SelectTrigger>
              {/* Popper, not the default item-aligned placement: that lines the
                  list up against a SelectValue, which this text trigger has none
                  of, and without one the list lands in the screen's corner. */}
              <SelectContent position="popper" align="end">
                {groups.map((group) => (
                  <SelectItem
                    key={yearKey(group.year)}
                    value={yearKey(group.year)}>
                    {group.year ?? 'Undated'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </span>
      }
      body={
        !active ? (
          <p className="text-muted-foreground py-2 text-center text-sm">
            Not evaluated — this client isn&rsquo;t in any measure population.
          </p>
        ) : active.open.length === 0 &&
          active.met.length === 0 &&
          active.unknown.length === 0 ? (
          <p className="text-muted-foreground py-2 text-center text-sm">
            No measures recorded for {active.year ?? 'this period'}.
          </p>
        ) : (
          <div className="flex flex-col gap-2.5">
            <GapGroup
              heading="Open"
              gaps={active.open}
              tone="open"
              badge="warning"
            />
            <GapGroup
              heading="Met"
              gaps={active.met}
              tone="met"
              badge="success"
            />
            <GapGroup
              heading="Unrecognized status"
              gaps={active.unknown}
              tone="unknown"
              badge="secondary"
            />
          </div>
        )
      }
    />
  )
}
